/**
 * Customer details schemas
 */

import { z } from 'zod';
import {
    CustomerDetailsStorageUSStateV1Values,
    CustomerDetailsStorageCanadaProvinceV1Values,
    type CustomerDetails,
    type CustomerDetailsStorageV1,
    type CustomerDetailsStorageV3,
    type CustomerDetailsStorageAddressV1,
} from '../payment.types';
import type { SafeParseResult } from '../zod-utils';
import { PhoneNumberSchema } from './common.schemas';

const CustomerEmailSchema: z.ZodString = z.string().email();

// Historical validators must not follow changes to Current or request primitives.
const CustomerDetailsStoragePhoneV1Schema: z.ZodType<string> = z.preprocess(
    (value: unknown) => {
        const stringParseResult: SafeParseResult<string> = z.string().safeParse(value);
        if (!stringParseResult.success) {
            return value;
        }
        const digitsOnly: string = stringParseResult.data.replace(/\D/g, '');
        if (digitsOnly.length !== 10) {
            return stringParseResult.data;
        }
        return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
    },
    z.string().regex(/^\(\d{3}\) \d{3}-\d{4}$/)
);

const CustomerDetailsStorageAddressV1Schema: z.ZodType<CustomerDetailsStorageAddressV1> =
    z.discriminatedUnion('country', [
        z.object({
            country: z.literal('CA'),
            street: z.string(),
            street2: z.string().optional(),
            city: z.string(),
            province: z.enum(CustomerDetailsStorageCanadaProvinceV1Values),
            postal_code: z
                .string()
                .regex(
                    /^[A-Z]\d[A-Z] ?\d[A-Z]\d$/i,
                    'Invalid Canadian Postal Code (Expected Format: A1A 1A1)'
                ),
        }),
        z.object({
            country: z.literal('US'),
            street: z.string(),
            street2: z.string().optional(),
            city: z.string(),
            state: z.enum(CustomerDetailsStorageUSStateV1Values),
            zip: z.union([z.string().regex(/^\d{5}$/), z.string().regex(/^\d{5}-\d{4}$/)]),
        }),
    ]);

const CustomerDetailsStorageV1Fields = {
    customerName: z.string().optional(),
    mainPhone: CustomerDetailsStoragePhoneV1Schema.optional(),
    otherPhones: z.array(CustomerDetailsStoragePhoneV1Schema).default([]),
    mainEmail: z.string().email().optional(),
    address: CustomerDetailsStorageAddressV1Schema.optional(),
    billingAddress: CustomerDetailsStorageAddressV1Schema.optional(),
};

export const CustomerDetailsStorageV1Schema: z.ZodType<CustomerDetailsStorageV1> = z
    .object(CustomerDetailsStorageV1Fields)
    .strict();

const CustomerDetailsStorageV3Fields = {
    ...CustomerDetailsStorageV1Fields,
    otherEmails: z.array(z.string().email()).default([]),
};

export const CustomerDetailsStorageV3DetailsSchema: z.ZodType<CustomerDetailsStorageV3['details']> =
    z.object(CustomerDetailsStorageV3Fields).strict();

export const CustomerDetailsSchema: z.ZodType<CustomerDetails> = z
    .object(CustomerDetailsStorageV3Fields)
    .strict();

export const CustomerContactRequiredErrorMessage =
    'At least one valid phone number or email address is required to save a card.';

export const customerDetailsHasContact = (customerDetails: CustomerDetails): boolean =>
    (customerDetails.mainPhone !== undefined &&
        PhoneNumberSchema.safeParse(customerDetails.mainPhone).success) ||
    (customerDetails.mainEmail !== undefined &&
        CustomerEmailSchema.safeParse(customerDetails.mainEmail).success);
